import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';

/** Makes each fold's body id unique, so two folds on one screen never collide on `aria-controls`. */
let nextFoldId = 0;

/** Which finder's colour the fold button carries: the Grants teal or the Jobs orange (`--agu-morebtn-*`). */
export type AGURailFoldTone = 'grants' | 'jobs';

/**
 * The finder rails' "More filters" fold: a full-width button with a chevron over a body of the rarely-used filter groups. It sits
 * INSIDE a `ra-*-filters` component between the always-visible groups and the `Show` switches, so it is its own component rather
 * than a slot of `agu-filter-rail` (content projected into the rail can only reach the rail's own `ng-content`, not a child
 * component's template).
 *
 * P/grant-finder.html:1035 (markup), :1591-1596 (script), :297-304 (CSS); the Jobs colour variant is P/job-finder.html:465-472.
 * The body opens with a 200 ms `moreIn`; the label reads "Fewer filters" while open.
 */
@Component({
    standalone: false,
    selector: 'agu-rail-fold',
    templateUrl: './rail-fold.component.html',
    styleUrl: './rail-fold.component.scss',
})
export class AGURailFoldComponent implements OnChanges {
    /** The closed-state label. */
    @Input() FoldLabel = 'More filters';
    /** The open-state label (P/grant-finder.html:1593). */
    @Input() FoldOpenLabel = 'Fewer filters';
    /**
     * True while any folded filter is set: the fold then opens by itself (P/grant-finder.html:1596, P/job-finder.html:1517). It only ever
     * opens — turning it false does not close a fold the member opened, and the member can still close an auto-opened one.
     */
    @Input() FoldOpen = false;
    /** Grants teal or Jobs orange. */
    @Input() Tone: AGURailFoldTone = 'grants';

    /** Whether the body is showing. */
    Open = false;

    readonly BodyId = `agu-fold-body-${(nextFoldId += 1)}`;

    get Label(): string {
        return this.Open ? this.FoldOpenLabel : this.FoldLabel;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['FoldOpen'] && this.FoldOpen) {
            this.Open = true;
        }
    }

    Toggle(): void {
        this.Open = !this.Open;
    }
}
